/**
 * IntensiveTrainingModal - Mode entraînement intensif de prononciation
 * Fonctionnalités: 10 mots, niveaux, focus, évaluation stricte, rapport final
 */
import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Target, 
  Volume2, 
  Mic, 
  MicOff, 
  Loader2, 
  ChevronRight, 
  RefreshCw 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntensiveTrainingModal = ({ isOpen, onClose }) => {
  const [phase, setPhase] = useState("setup"); // setup, training, report
  const [difficulty, setDifficulty] = useState("beginner");
  const [focus, setFocus] = useState("mixed");
  const [isLoading, setIsLoading] = useState(false);
  const [trainingSession, setTrainingSession] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [attempts, setAttempts] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [currentAttempt, setCurrentAttempt] = useState(null);
  const [report, setReport] = useState(null);
  const mediaRecorderRef = useRef(null);

  const difficultyOptions = [
    { value: "beginner", label: "Débutant", emoji: "🌱", desc: "Mots simples et courts" },
    { value: "intermediate", label: "Intermédiaire", emoji: "🌿", desc: "Mots de longueur moyenne" },
    { value: "advanced", label: "Avancé", emoji: "🌳", desc: "Mots complexes et longs" }
  ];

  const focusOptions = [
    { value: "mixed", label: "Mix complet", emoji: "🎯" },
    { value: "tones", label: "Tons", emoji: "🎵" },
    { value: "consonants", label: "Consonnes", emoji: "🔤" },
    { value: "vowels", label: "Voyelles", emoji: "🅰️" }
  ];

  const startTraining = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/training/start`, {
        difficulty,
        focus
      });
      setTrainingSession(response.data);
      setAttempts([]);
      setCurrentWordIndex(0);
      setPhase("training");
      toast.success("Entraînement prêt ! C'est parti ! 💪");
    } catch (err) {
      console.error("Training start error:", err);
      toast.error("Erreur lors du démarrage de l'entraînement");
    } finally {
      setIsLoading(false);
    }
  };

  const speakWord = async (word) => {
    try {
      const response = await axios.post(
        `${API}/tts?text=${encodeURIComponent(word)}&speed=0.7`,
        null,
        { responseType: 'blob' }
      );
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audio.onended = () => URL.revokeObjectURL(audioUrl);
      await audio.play();
    } catch (err) {
      console.error("TTS error:", err);
    }
  };

  const startRecording = async () => {
    try {
      // Vérifier si le navigateur supporte getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error("Le microphone n'est pas disponible sur ce navigateur.");
        return;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];

      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        // Convert to base64 and send for evaluation
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Audio = reader.result.split(',')[1];
          await evaluateAttempt(base64Audio);
        };
        reader.readAsDataURL(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Recording error:", err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        toast.error("Accès au microphone refusé. Autorise le micro dans les réglages du navigateur, puis réessaie.");
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        toast.error("Aucun microphone détecté. Vérifie que ton micro est branché.");
      } else if (window.location.hostname.includes('preview.emergentagent.com')) {
        toast.error("Le microphone peut être bloqué dans l'aperçu. Teste cette fonction après déploiement sur l'URL réelle.");
      } else {
        toast.error("Erreur d'accès au microphone. Vérifie les permissions de ton navigateur.");
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const evaluateAttempt = async (audioBase64) => {
    setIsLoading(true);
    try {
      const currentWord = trainingSession.words[currentWordIndex];
      const response = await axios.post(`${API}/training/attempt`, {
        session_id: trainingSession.session_id,
        word_index: currentWordIndex,
        audio_base64: audioBase64,
        expected_thai: currentWord.thai
      });
      
      setCurrentAttempt(response.data);
      
      // Store attempt
      setAttempts(prev => [...prev, {
        word: currentWord.thai,
        score: response.data.score,
        transcription: response.data.transcription,
        is_correct: response.data.is_correct
      }]);
      
      if (response.data.is_correct) {
        toast.success(`${response.data.score}/10 - Bien ! 👏`);
      } else {
        toast.error(`${response.data.score}/10 - À améliorer...`);
      }
    } catch (err) {
      console.error("Evaluation error:", err);
      toast.error("Erreur lors de l'évaluation");
    } finally {
      setIsLoading(false);
    }
  };

  const nextWord = () => {
    if (currentWordIndex < trainingSession.words.length - 1) {
      setCurrentWordIndex(prev => prev + 1);
      setCurrentAttempt(null);
    } else {
      generateReport();
    }
  };

  const generateReport = async () => {
    setIsLoading(true);
    setPhase("report");
    try {
      const response = await axios.post(`${API}/training/report`, {
        session_id: trainingSession.session_id,
        attempts: attempts
      });
      setReport(response.data);
    } catch (err) {
      console.error("Report error:", err);
      toast.error("Erreur lors de la génération du rapport");
    } finally {
      setIsLoading(false);
    }
  };

  const resetTraining = () => {
    setPhase("setup");
    setTrainingSession(null);
    setCurrentWordIndex(0);
    setAttempts([]);
    setCurrentAttempt(null);
    setReport(null);
  };

  const currentWord = trainingSession?.words?.[currentWordIndex];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl bg-gradient-to-br from-orange-50 to-red-50 border-none max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Target className="w-6 h-6 text-orange-600" />
            Entraînement Intensif
          </DialogTitle>
          <DialogDescription>
            Pratique ta prononciation avec Manee - Mode strict !
          </DialogDescription>
        </DialogHeader>

        {phase === "setup" && (
          <div className="space-y-6 py-4">
            {/* Difficulty Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Niveau de difficulté
              </label>
              <div className="grid grid-cols-3 gap-2">
                {difficultyOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setDifficulty(opt.value)}
                    className={`p-3 rounded-xl border-2 transition-all text-center ${
                      difficulty === opt.value
                        ? 'border-orange-500 bg-orange-100 text-orange-800'
                        : 'border-slate-200 bg-white hover:border-orange-300'
                    }`}
                  >
                    <span className="text-2xl block mb-1">{opt.emoji}</span>
                    <span className="text-sm font-medium">{opt.label}</span>
                    <span className="text-xs text-slate-500 block">{opt.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Focus Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Focus d'entraînement
              </label>
              <div className="grid grid-cols-4 gap-2">
                {focusOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setFocus(opt.value)}
                    className={`p-2 rounded-xl border-2 transition-all text-center ${
                      focus === opt.value
                        ? 'border-orange-500 bg-orange-100 text-orange-800'
                        : 'border-slate-200 bg-white hover:border-orange-300'
                    }`}
                  >
                    <span className="text-xl block">{opt.emoji}</span>
                    <span className="text-xs font-medium">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Start Button */}
            <Button
              onClick={startTraining}
              disabled={isLoading}
              className="w-full rounded-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
              data-testid="start-training-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Préparation...
                </>
              ) : (
                <>
                  <Target className="w-5 h-5 mr-2" />
                  Commencer l'entraînement (10 mots)
                </>
              )}
            </Button>
          </div>
        )}

        {phase === "training" && currentWord && (
          <div className="space-y-6 py-4">
            {/* Progress Bar */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-600">
                Mot {currentWordIndex + 1}/10
              </span>
              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all"
                  style={{ width: `${((currentWordIndex + 1) / 10) * 100}%` }}
                />
              </div>
            </div>

            {/* Current Word Card */}
            <div className="bg-white rounded-2xl p-6 border-2 border-orange-200 text-center">
              <p className="font-thai text-5xl text-orange-800 mb-2">{currentWord.thai}</p>
              <p className="text-sm text-slate-500">{currentWord.french}</p>
              <div className="mt-3 inline-block px-3 py-1 bg-orange-100 rounded-full text-sm text-orange-700">
                ⚠️ {currentWord.focus_point}
              </div>
            </div>

            {/* Controls */}
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => speakWord(currentWord.thai)}
                variant="outline"
                className="rounded-full"
              >
                <Volume2 className="w-5 h-5 mr-2" />
                Écouter
              </Button>
              
              <Button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isLoading}
                className={`rounded-full ${
                  isRecording 
                    ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                    : 'bg-gradient-to-r from-orange-500 to-red-500'
                }`}
              >
                {isRecording ? (
                  <>
                    <MicOff className="w-5 h-5 mr-2" />
                    Arrêter
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>

            {/* Current Attempt Result */}
            {currentAttempt && (
              <div className={`rounded-xl p-4 border-2 ${
                currentAttempt.is_correct 
                  ? 'bg-green-50 border-green-300' 
                  : 'bg-red-50 border-red-300'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-bold">
                    {currentAttempt.is_correct ? '✅ Correct !' : '❌ À améliorer'}
                  </span>
                  <span className={`text-2xl font-bold ${
                    currentAttempt.score >= 7 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {currentAttempt.score}/10
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-2">
                  <span className="font-medium">Tu as dit :</span> "{currentAttempt.transcription}"
                </p>
                <p className="text-sm text-slate-700">{currentAttempt.feedback}</p>
                {currentAttempt.corrections.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-red-600">Corrections :</p>
                    <ul className="text-xs text-red-600 list-disc list-inside">
                      {currentAttempt.corrections.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Next Button */}
            {currentAttempt && (
              <Button
                onClick={nextWord}
                className="w-full rounded-full"
              >
                {currentWordIndex < 9 ? (
                  <>Mot suivant <ChevronRight className="w-4 h-4 ml-1" /></>
                ) : (
                  <>Voir mon rapport 📊</>
                )}
              </Button>
            )}
          </div>
        )}

        {phase === "report" && (
          <div className="space-y-6 py-4">
            {isLoading ? (
              <div className="text-center py-8">
                <Loader2 className="w-10 h-10 animate-spin text-orange-500 mx-auto mb-4" />
                <p className="text-slate-600">Génération du rapport...</p>
              </div>
            ) : report && (
              <>
                {/* Score Card */}
                <div className={`rounded-2xl p-6 text-center ${
                  report.grade.startsWith('A') ? 'bg-green-100 border-2 border-green-300' :
                  report.grade.startsWith('B') ? 'bg-blue-100 border-2 border-blue-300' :
                  report.grade.startsWith('C') ? 'bg-yellow-100 border-2 border-yellow-300' :
                  'bg-red-100 border-2 border-red-300'
                }`}>
                  <div className="text-6xl mb-2">
                    {report.grade.startsWith('A') ? '🏆' :
                     report.grade.startsWith('B') ? '👍' :
                     report.grade.startsWith('C') ? '💪' : '📚'}
                  </div>
                  <p className="text-4xl font-bold text-slate-800">
                    {report.total_score.toFixed(1)}/10
                  </p>
                  <p className="text-2xl font-bold text-slate-600">Note : {report.grade}</p>
                </div>

                {/* Summary */}
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <p className="text-slate-700">{report.summary}</p>
                </div>

                {/* Strengths & Weaknesses */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <h4 className="font-medium text-green-800 mb-2">💪 Points forts</h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      {report.strengths.map((s, i) => (
                        <li key={i}>• {s}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                    <h4 className="font-medium text-red-800 mb-2">⚠️ À améliorer</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      {report.weaknesses.map((w, i) => (
                        <li key={i}>• {w}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-2">📝 Recommandations de Manee</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    {report.recommendations.map((r, i) => (
                      <li key={i}>• {r}</li>
                    ))}
                  </ul>
                </div>

                {/* Next Focus */}
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-200 text-center">
                  <p className="text-sm text-purple-600">Prochain objectif :</p>
                  <p className="text-lg font-medium text-purple-800">{report.next_focus}</p>
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={resetTraining}
                    className="flex-1 rounded-full bg-gradient-to-r from-orange-500 to-red-500"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Recommencer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="flex-1 rounded-full"
                  >
                    Fermer
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IntensiveTrainingModal;
